// The ONE tier truth — every surface that speaks about price reads this shape,
// so a number changes in exactly one place: the /pricing page, the investor
// deck's pricing chart, checkout and any cap gate tomorrow.
//
// MODEL v2 (decided 2026-08-30 by John; supersedes the free + plus $8 shape).
// innernet is sold as FOUR DOORS onto one memory. The branch is chosen by *who
// the memory is about and who owns it* — never by persona. "Developer" is not a
// tier, because a developer is also a person. The branches NEST: teams ⊃ people
// (every seat carries a whole personal innernet), enterprise ⊃ teams (plus
// control over where the data lives). `products` sits outside the chain — a
// different subject, a different meter.
//
// THE UNIT IS A FOLD, NOT A CELL. A fold is one memory-mutating pass (netti's
// thinking). Measured against live usage, folds correlate 0.988 with real
// spend; cells only 0.774. So: you pay for innernet THINKING. Never for
// innernet remembering (writes are free), never for being asked (reads are
// free, forever — the competitors meter both).
//
// Pure and serializable: no Supabase, no env, no React — it imports cleanly
// into server and client alike.

export type BranchId = 'people' | 'products' | 'teams' | 'enterprise'

export interface Branch {
  id: BranchId
  /** the public name — the ONLY name that goes out for this branch */
  name: string
  /** who the memory is about */
  about: string
  /** who owns that memory */
  ownedBy: string
  /** the price, spoken */
  price: string
  /** what grows the bill */
  meter: string
  /** the one line of what this door adds */
  note: string
  /** true while the number is proposed rather than decided */
  open?: boolean
}

/* ── the numbers, once ─────────────────────────────────────────────────── */

/** folds a free personal innernet gets each month. john set it on 2026-10-21
 *  for the launch: 200 free (it was 500). past it, netti stops folding and the
 *  memory reads but does not take new writes until the month turns (john,
 *  2026-10-29: "you can only read from innernet's memory for free"). */
export const FREE_FOLD_CAP = 200
/** the paid personal ceiling: none. john, 2026-10-29: "$14 with unlimited folds
 *  for now, since we don't have a bigger plan to restrict them" (it was 1,200, 6×
 *  the free plan, and 150,000 before that). */
export const PLUS_FOLD_CAP: number | null = null
/** the paid personal step — deliberately BELOW the $20 ai-subscription band */
export const PERSONAL_PRICE = 14
/** per user, per month, for a team seat (carries a whole personal innernet) */
export const TEAM_SEAT_PRICE = 20
/** people a room holds on the free plan, its owner included. john set it on
 *  2026-10-05: up to this many is free, the next person needs the team plan.
 *  migration 136's accept_space_invite enforces the same number in the
 *  database — change the two together. */
export const ROOM_FREE_SEATS = 4
/** proposed entry for the products branch — NOT yet decided */
export const PRODUCTS_ENTRY_PRICE = 29
/** the enterprise floor, written for 25–100 people */
export const ENTERPRISE_FLOOR = 25_000
/** blended cost of a thousand folds today (~$1.90 after two known fixes) */
export const FOLD_COST_PER_1K = 3.21

const ORDINAL_WORDS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth']

/** 5 → "fifth": the person who tips a free room over, spoken. */
function ordinal(n: number): string {
  if (n >= 1 && n <= ORDINAL_WORDS.length) return ORDINAL_WORDS[n - 1]
  const teen = n % 100 >= 11 && n % 100 <= 13
  return `${n}${teen ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')}`
}

export const BRANCHES: Branch[] = [
  {
    id: 'people',
    name: 'innernet for people',
    about: 'you',
    ownedBy: 'you',
    price: `free · $${PERSONAL_PRICE}/mo`,
    meter: 'folds',
    note: `the whole product, ${FREE_FOLD_CAP} folds a month free. plus takes the ceiling away.`,
  },
  {
    id: 'products',
    name: 'innernet for products',
    about: 'the people who use your app',
    ownedBy: 'them, or you',
    price: `from $${PRODUCTS_ENTRY_PRICE}/mo`,
    meter: 'people held',
    note: 'memory inside your product — your users keep accounts they own, and reads are never billed.',
    open: true,
  },
  {
    id: 'teams',
    name: 'innernet for teams',
    about: 'a body of shared work',
    ownedBy: 'the company',
    price: `$${TEAM_SEAT_PRICE} / user / mo`,
    meter: 'seats + agent seats',
    note: `a room is free for up to ${ROOM_FREE_SEATS} people. a ${ordinal(ROOM_FREE_SEATS + 1)} needs the team plan — $${TEAM_SEAT_PRICE} per person, per month. shared spaces, sso, audit trails, attributed branch and merge. agent seats are the expansion.`,
  },
  {
    id: 'enterprise',
    name: 'innernet for enterprise',
    about: 'the same, on your own ground',
    ownedBy: 'the company',
    price: `from $${(ENTERPRISE_FLOOR / 1000).toFixed(0)}k / yr`,
    meter: 'the contract',
    note: 'your servers, your region, your retention policy. written for 25 to 100 people.',
  },
]

/** the ownership law — why teams never cancel out people. */
export const OWNERSHIP_LAW =
  'a company can pay for your seat. it can never own your memory.'

/* ── the personal branch, as the two cards /pricing shows ───────────────── */

export type PlanId = 'free' | 'plus'

export interface Plan {
  id: PlanId
  /** the tier's public name, lowercase like everything else */
  name: string
  /** whole dollars per month; 0 = free */
  pricePerMonth: number
  /** folds this tier gets each month; null = uncounted (no tier is, today) */
  foldCap: number | null
  /** months free before the first charge; 0 = no trial */
  trialMonths: number
  /** the marker above the name — the one thing to notice about this tier */
  mark: string
  /** the one line under the price */
  tagline: string
  /** only what this tier adds. the shared product is BASE_FEATURES. */
  points: string[]
  cta: { label: string; href: string }
}

export const PLUS_PRICE = PERSONAL_PRICE
/** no free months. john, 2026-10-29: "we are not giving anybody a free innernet plus for 3 months since the
 *  product is already free to try" (it was 3). a promise made by hand (plan_promises, 160) is still kept. */
export const PLUS_TRIAL_MONTHS = 0

/* ── the introductory price, the one door that is never on a public page ──── */
//
// john, 2026-10-29: a free person who reaches INTRO_TRIGGER_FOLDS folds in a month gets one letter: the first
// INTRO_SEATS people to join plus pay INTRO_MONTHLY_PRICE a month or INTRO_YEARLY_PRICE a year. it is unlocked by
// that letter's link (or a code a holder shares, INTRO_SHARES people each), and only then shown, inside settings →
// plan. every public surface keeps saying PERSONAL_PRICE.

/** folds in a calendar month that bring the letter */
export const INTRO_TRIGGER_FOLDS = 100
/** people who can join at the introductory price, ever */
export const INTRO_SEATS = 500
/** whole dollars, monthly */
export const INTRO_MONTHLY_PRICE = 9
/** whole dollars, yearly */
export const INTRO_YEARLY_PRICE = 99
/** people one holder of the letter can pass the price to, by code */
export const INTRO_SHARES = 2

export type BillingPeriod = 'monthly' | 'yearly'
/** which price a razorpay plan is: the list price everyone sees, or the introductory one */
export type PriceOffer = 'list' | 'intro'

/** the product itself — what every innernet is, at any price, in any branch. */
export const BASE_FEATURES: string[] = [
  'one memory across every ai — claude, chatgpt, cursor, anything that speaks mcp',
  'personal memory that learns how you work, not just what you saved',
  'project context maps the agent keeps current on its own',
  'versioned thinking — commits, branches, directions parked and merged back',
  'living artifacts that reorganise themselves as findings land',
  'an activity feed that narrates what was learned, written to be read',
  'import your scattered claude and chatgpt history in one pass',
  'private by default — row-level security, export or delete anytime',
]

export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'innernet',
    pricePerMonth: 0,
    foldCap: FREE_FOLD_CAP,
    trialMonths: 0,
    mark: 'no card required',
    tagline: 'the whole product, with room to become someone.',
    points: [
      'the entire product, nothing held back',
      `${FREE_FOLD_CAP} folds a month: you are only counted when netti thinks`,
      'reading is free forever, even past the folds',
      'no card, no clock, no expiry',
    ],
    cta: { label: 'begin', href: '/signup' },
  },
  {
    id: 'plus',
    name: 'innernet plus',
    pricePerMonth: PERSONAL_PRICE,
    foldCap: PLUS_FOLD_CAP,
    trialMonths: PLUS_TRIAL_MONTHS,
    mark: 'unlimited folds',
    tagline: 'everything netti can think for you, and agents that remember.',
    points: [
      'unlimited folds: netti thinks as often as you write',
      'agent memory: build agents that remember the people they serve, on the same plan',
      'the deeper agent passes: heavier consolidation, sharper recall',
      'every feature we ship next, the day it ships',
      'a straight line to the people who built it',
    ],
    cta: { label: 'move to plus', href: '/signup?plan=plus' },
  },
]

export const FREE_PLAN = PLANS[0]
export const PLUS_PLAN = PLANS[1]

/** how a tier's room reads on a surface: "200" free, "∞" plus. */
export function foldFigure(plan: Plan): string {
  return plan.foldCap === null ? '∞' : plan.foldCap.toLocaleString('en-US')
}

/** the price line, spoken: "free forever" · "$14 a month". */
export function priceLine(plan: Plan): string {
  return plan.pricePerMonth === 0 ? 'free forever' : `$${plan.pricePerMonth} a month`
}
